"use client";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";

// assets
import {
  BoxetteLogoDark,
  CloseModalIcon,
  DeleteIcon,
  EasyXpressLogo,
  EditIcon,
} from "@/components/assets/icons";
import { ShowWhen } from "../show-when/show-when";
import { Theme } from "@/enums";
import { useTheme } from "@/context/Theme.context";
import { useTranslations } from "next-intl";

type TProps = {
  children: React.ReactNode;
  isOpen: boolean;
  closeModal: () => void;
  title?: React.ReactNode;
  showModalHeader?: boolean;
  headerPaddings?: string;
  paddings?: string;
  className?: string;
  titleClassNames?: string;
  fullScreen?: boolean;
  showActions?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  showCloseIcon?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  needsDeclaration?: boolean;
};

const Modal = ({
  children,
  isOpen,
  title,
  showModalHeader,
  headerPaddings,
  paddings,
  closeModal,
  className,
  titleClassNames,
  fullScreen,
  showActions = false,
  onEdit,
  onDelete,
  showCloseIcon,
  showEdit,
  showDelete,
  needsDeclaration,
}: TProps) => {
  const theme = useTheme();
  const modalRef = useRef(null);

  const t = useTranslations();

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // disable background scroll
  useEffect(() => {
    document.body.classList.add("no-scroll");
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, []);

  // const outsideClick = (event: { target: any }) => {
  //   if (modalRef.current && !modalRef.current.contains(event.target)) {
  //     closeModal();
  //   }
  // };

  if (!mounted || !isOpen) {
    return null;
  }

  return createPortal(
    <div
      className="modal-bg modal fixed inset-0 z-[9999] flex items-center justify-center"
      // onClick={outsideClick}
    >
      <div
        ref={modalRef}
        // className={classNames(
        //   "relative overflow-y-auto rounded-[2.5rem] bg-white",
        //   className,
        //   !fullScreen ? "max-h-[calc(100vh-120px)]" : "h-screen w-screen",
        // )}
        className={classNames(
          "relative overflow-y-auto rounded-[2.5rem] bg-white",
          className,
          !fullScreen
            ? "max-h-[calc(100vh-120px)]"
            : "fixed inset-0 h-[100dvh] w-[100dvw] overflow-hidden",
        )}
      >
        <div
          className={classNames(
            "sticky left-0 top-0 z-20 flex w-full flex-row items-center bg-white py-[26px]",
            headerPaddings,
            {
              "justify-end": !title,
              "justify-between": title,
              "px-[26px]": !showModalHeader,
              "border border-neutral-100": showModalHeader,
            },
          )}
        >
          <ShowWhen condition={!!title}>
            <h1 className={classNames("text-base font-bold", titleClassNames)}>
              {title ? t(title) : ""}
            </h1>
          </ShowWhen>
          <ShowWhen condition={showModalHeader && !showActions && !title}>
            <div className="sticky top-0 mx-auto flex w-[77.32%] max-w-[1486px] justify-between">
              <ShowWhen
                condition={theme === Theme.GEORGIA || theme === Theme.UZ}
                elseRender={
                  <EasyXpressLogo className="h-[2.125rem] w-[8.75rem]" />
                }
              >
                <BoxetteLogoDark className="h-[2.125rem] w-[8.75rem]" />
              </ShowWhen>
              <div
                onClick={closeModal}
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-zinc-100"
              >
                <CloseModalIcon className="h-[6] w-6" />
              </div>
            </div>
          </ShowWhen>
          <ShowWhen condition={!!showCloseIcon}>
            <div
              onClick={closeModal}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-zinc-100"
            >
              <CloseModalIcon className="h-[6] w-6" />
            </div>
          </ShowWhen>
          <ShowWhen condition={!!showActions}>
            <div className="flex gap-4">
              <div className="flex items-center gap-4">
                <ShowWhen condition={!!showDelete}>
                  <span
                    onClick={onDelete}
                    className="cursor-pointer rounded-full bg-[#f7ebeb] p-2"
                  >
                    <DeleteIcon />
                  </span>
                </ShowWhen>
                <ShowWhen condition={!!showEdit}>
                  <span
                    onClick={onEdit}
                    className={classNames(
                      "cursor-pointer rounded-full p-2 text-[14px] text-[#08a7bc]",
                      {
                        "bg-[#F9D6BD]": theme === Theme.UK,
                        "bg-[#e7f6f8]": theme !== Theme.UK,
                      },
                    )}
                  >
                    <EditIcon
                      // className={`fill-[${theme === Theme.UK ? "" : "#13cfe8"}]`}
                      className={classNames("inline-block", {
                        "fill-[#EB7623]": theme === Theme.UK,
                        "fill-[#13CFE8]": theme !== Theme.UK,
                      })}
                    />
                    {needsDeclaration && t("declaration")}
                  </span>
                </ShowWhen>
                <ShowWhen condition={!!showDelete || !!showEdit}>
                  <div className="h-6 w-[1px] bg-[#eaeaea]" />
                </ShowWhen>
              </div>
              <div
                onClick={closeModal}
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-zinc-100"
              >
                <CloseModalIcon className="h-[6] w-6" />
              </div>
            </div>
          </ShowWhen>
        </div>
        <div
          className={classNames(paddings, {
            "px-[26px]": !showModalHeader,
            "mx-auto w-[77.32%] max-w-[1486px] py-[74px]": showModalHeader,
          })}
        >
          {children}
        </div>
      </div>
    </div>,
    document.getElementById("modal-root")!,
  );
};

export { Modal };
